/**
 * Run setup — choose a body, choose a world, spend Attunement, roll a seed.
 *
 * docs/12 §5 records the reason this file exists: all 17 archetypes and both
 * world modes are implemented and reachable only through `?archetype=` and
 * `?mode=`. A headline feature the player never sees is not a feature.
 *
 * Two decisions drive the whole layout:
 *
 *   1. **The archetype list is the screen.** docs/02 §3.2 — "body plan is not a
 *      skin". The player is choosing a *way to move*, so the copy is movement
 *      copy: "−18% speed, +40% mass, walks through loose matter, starts with
 *      Breach". A stat table would say the same thing and teach none of it.
 *   2. **Locked bodies still render.** Lineage unlocks what killed you
 *      (docs/05 §4), which only works as a motive if you can see the thing you
 *      haven't earned and read why. Hiding locked rows would throw away the
 *      entire progression read of the screen.
 *
 * Movement of the cursor *is* selection (list-box semantics), which is what
 * frees Enter to mean "begin" from anywhere — the fastest path to a run is one
 * keystroke. Locked rows are the one exception: the cursor may land on them to
 * read the hint, but selection stays on the last legal body and the footer says
 * plainly what will actually start.
 *
 * Follows the panel convention in uiRoot.ts: this screen reports intent through
 * callbacks and mutates no game state. It imports the stylesheet/DOM kit and
 * nothing else — the integrator maps real data into the view models below.
 */

import { btn, clear, el, injectStyle, Pool, TextBind } from './style';

// ------------------------------------------------------------------ view models

export type ArchetypeKind = 'humanoid' | 'weird';

export interface ArchetypeChoiceVM {
  id: string;
  name: string;
  kind: ArchetypeKind;
  /** The one-sentence mechanical hook (`ArchetypeDef.special`). Shown large. */
  special: string;
  /**
   * Plain-language movement phrases, already written for a player:
   * `['−18% speed', '+40% mass', 'walks through loose matter']`. Ordered by
   * what the player will notice first. The adapter writes these, not the UI —
   * only the adapter knows which deltas are actually worth a sentence.
   */
  movement: string[];
  /** Display names of abilities this body starts with: `['Breach']`. */
  innate: string[];
  locked?: boolean;
  /** Why it is locked and what earns it: "Lineage — die to a fungal beast". */
  lockHint?: string;
}

export interface WorldModeChoiceVM {
  id: string;
  name: string;
  /** What the generator actually does differently (docs/05 §1). */
  description: string;
  /** The one-line feel: "archaeological · strange but coherent". */
  feel: string;
  locked?: boolean;
  lockHint?: string;
}

export interface AbilityChoiceVM {
  id: string;
  name: string;
  /** What it opens, in level-design terms: "wide gaps, 3-cell ceiling gaps". */
  desc: string;
  locked?: boolean;
  lockHint?: string;
}

/**
 * A starting loadout. The body is *how you move*; the kit is *what you are
 * holding on frame one*, and until this screen offered one the answer was
 * always the same two weapons — which made the whole salvage economy invisible
 * at the moment it is most legible.
 */
export interface KitChoiceVM {
  id: string;
  name: string;
  /** One sentence: what you are holding on frame one. */
  desc: string;
  locked?: boolean;
  lockHint?: string;
}

/**
 * An opt-in world modifier (#7).
 *
 * Opt-in is the whole design and the reason `enabled` is a toggle rather than a
 * roll: a modifier that turns up unannounced is a bug report, not a feature —
 * the player cannot tell a deliberate rule change from a broken generator. And
 * `cost` is rendered, never dropped, because an opt-in challenge with no stated
 * downside is a trap rather than a choice.
 */
export interface WorldModifierChoiceVM {
  id: string;
  name: string;
  desc: string;
  /** The one-line downside. Beside the toggle, not buried in a tooltip. */
  cost: string;
  /** Selected for the next run. */
  enabled: boolean;
  locked: boolean;
  lockHint?: string;
}

/**
 * A currency, described rather than assumed (#10).
 *
 * There are two and they must never read alike: one is spent inside a run and
 * dies with you, one is banked forever and is what this screen spends. A panel
 * that invents its own labels is how a player comes to spend a permanent unlock
 * as if it were pocket change, so the glyph, the scope word and the sentence
 * about death all arrive from the integrator, which owns both currencies.
 */
export interface CurrencyVM {
  id: string;
  name: string;
  /** Two words, printed beside the number. Never the same for both currencies. */
  scope: string;
  /** What happens to it when you die — the distinction, in one sentence. */
  blurb: string;
  /** So the two are told apart at a glance and not only by reading. */
  glyph: string;
}

export interface ShopEntryVM {
  id: string;
  name: string;
  cost: number;
  /** Payable from the current balance right now. Never doubles as "allowed". */
  affordable: boolean;
  /** The unlock's own flavour copy. */
  description: string;
  /** What it mechanically hands over: "3 stash slots that survive death". */
  grant: string;
}

export interface AshShopVM {
  currency: CurrencyVM;
  /** The other currency, so the screen states the distinction it did not invent. */
  contrast: CurrencyVM;
  balance: number;
  entries: ShopEntryVM[];
  /** Why the list is empty, when it is. An unexplained empty shop reads broken. */
  empty: string | null;
  /** The last purchase attempt's result, success or refusal. */
  message: string | null;
}

export interface RunSetupVM {
  archetypes: ArchetypeChoiceVM[];
  kits: KitChoiceVM[];
  worldModes: WorldModeChoiceVM[];
  /** `slots` is the Attunement track's earned capacity; 0 on a fresh profile. */
  attunement: { slots: number; available: AbilityChoiceVM[] };
  /**
   * Opt-in world modifiers (#7). Optional on the same terms as `shop`: absent,
   * the section is hidden rather than shown empty.
   */
  modifiers?: WorldModifierChoiceVM[];
  /**
   * The between-runs Ash spend screen (#10). Optional so an integrator can
   * mount this screen before wiring progression to it; absent, the section is
   * hidden rather than rendered empty, because an empty shop and an unwired one
   * look identical and only one of them is a bug.
   */
  shop?: AshShopVM;
  seed: number;
  /**
   * Held across runs, so rerolling and re-entering the screen leave it alone.
   *
   * A locked seed is how you compare two runs of the *same* world — the thing
   * every sweep in `tests/` does offline and nobody could do by playing.
   */
  seedLocked: boolean;
}

export interface RunSetupCallbacks {
  onStart?(choice: {
    archetype: string;
    kit: string;
    mode: string;
    abilities: string[];
    seed: number;
  }): void;
  /** The integrator rolls the new seed and hands it back through `update`. */
  onReroll?(): void;
  /**
   * A seed typed in directly, already parsed and masked to uint32.
   *
   * Reported rather than applied, like every other choice on this screen: the
   * integrator owns the seed, and a screen that mutated it locally would give
   * two answers to "what world is this" the moment the two drifted.
   */
  onSeedEntered?(seed: number): void;
  /** Lock or unlock the seed against rerolling. */
  onToggleSeedLock?(locked: boolean): void;
  /**
   * Turn a world modifier on or off for the next run (#7). Reported rather than
   * applied: the pick is persisted on the profile, and the screen learns the
   * result through the next `update` like every other choice here.
   */
  onToggleModifier?(id: string, on: boolean): void;
  /**
   * Spend Ash on `id`. The screen reports intent only: the debit, the grant and
   * the refusal message all belong to progression, and the integrator hands the
   * result back through `update` — which is what makes a double-clicked button
   * debit once and say so the second time.
   */
  onPurchase?(id: string): void;
  /**
   * Leave without starting anything — Escape, or the Back button.
   *
   * The screen had no way out at all: it was the first thing a returning player
   * saw and the only exit was to begin a run. Reported rather than acted on,
   * like everything else here; the shell owns where "back" goes.
   */
  onBack?(): void;
}

/**
 * The currency stamp: glyph, amount, name, and the scope word.
 *
 * The scope word is the load-bearing part and the reason this is one function
 * rather than three template literals. "40 Ash" and "40 Scrip" differ by a noun
 * a new player has no reason to weight; "◆ 40 Ash · permanent" and
 * "○ 40 Scrip · this run" differ by the only thing that matters about them.
 */
export function currencyText(amount: number, c: CurrencyVM): string {
  return `${c.glyph} ${amount} ${c.name} · ${c.scope}`;
}

/** The price on a shop row. Same argument as `currencyText`, minus the scope. */
export function priceText(cost: number, c: CurrencyVM): string {
  return `${c.glyph} ${cost} ${c.name}`;
}

/**
 * Which kit the screen should be sitting on, given the offer and the current
 * pick. Pure, and exported, because this is the rule that actually matters: a
 * save-file load or an unlock can take a kit away under the screen, and a start
 * button pointing at one the profile cannot use is a bug that only surfaces on
 * the run itself. Keeps a legal pick, otherwise falls to the first unlocked —
 * which is always the Wanderer's Kit.
 */
export function defaultKitId(kits: readonly KitChoiceVM[], current: string | null): string | null {
  if (current && kits.some((k) => k.id === current && !k.locked)) return current;
  return kits.find((k) => !k.locked)?.id ?? null;
}

// ------------------------------------------------------------------ style

const STYLE_ID = 'noitroidvania-runsetup-style';

/**
 * Lives here rather than in style.ts because this screen is not part of the
 * in-run HUD and must not cost the HUD a byte of stylesheet. It inherits every
 * colour from the `.ui-root` custom properties, so a biome grade still tints it.
 */
const RUNSETUP_CSS = /* css */ `
.rs-layer { position: absolute; inset: 0; }

/* Head and foot wrap: at 480px the title, seed and start button do not fit on
   one line, and a non-wrapping flex row overflows the panel instead of
   stacking. */
.rs-panel .panel__head, .rs-panel .panel__foot { flex-wrap: wrap; row-gap: 4px; }

.rs-body { flex: 1 1 auto; display: flex; min-height: 0; min-width: 0; }
.rs-list { width: 272px; flex: none; border-right: 1px solid var(--ui-line); }
.rs-main { flex: 1 1 auto; }

@media (max-width: 820px) {
  /* No room for two columns. The list takes the top half and the detail
     scrolls under it — both keep their own scroller, so neither can push the
     other off the panel. */
  .rs-body { flex-direction: column; }
  .rs-list { width: auto; flex: 0 0 auto; max-height: 42%; border-right: none; border-bottom: 1px solid var(--ui-line-hot); }
}

.rs-sechead {
  padding: 3px 8px; font-size: var(--ui-fs-label); letter-spacing: .18em; text-transform: uppercase;
  color: var(--ui-fg-mute); background: var(--ui-panel-2);
  border-bottom: 1px solid var(--ui-line); border-top: 1px solid var(--ui-line);
}

.rs-arch {
  display: flex; align-items: center; gap: 6px;
  padding: 3px 8px; border-bottom: 1px solid var(--ui-line-soft); cursor: pointer;
}
.rs-arch:hover { background: var(--ui-panel-2); }
.rs-arch[data-cursor="1"] { background: var(--ui-panel-3); }
.rs-arch[data-selected="1"] { box-shadow: var(--ui-mark); }
.rs-arch[data-selected="1"] .rs-arch__n { color: var(--ui-accent); }
.rs-arch[data-locked="1"] { opacity: .42; }
/* A dimmed row still has to show where the cursor is, or browsing the bodies
   you have not earned yet is browsing blind. */
.rs-arch[data-locked="1"][data-cursor="1"] { opacity: .7; }
.rs-arch__g { width: 14px; flex: none; text-align: center; color: var(--ui-fg-mute); }
.rs-arch__b { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
.rs-arch__n { font-size: 11px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* The movement line is the read, so it gets a whole row of its own and
   truncates rather than wrapping the card taller than its neighbours. */
.rs-arch__s { font-size: var(--ui-fs-tiny); color: var(--ui-fg-mute); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.rs-detail { padding: 10px; display: flex; flex-direction: column; gap: 12px; }
.rs-dhead { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.rs-dname { font-size: 14px; letter-spacing: .16em; text-transform: uppercase; color: var(--ui-accent); }
.rs-arch[data-locked="1"] .rs-arch__g, .rs-lockchip { color: var(--ui-warn); }
.rs-lockchip { border-color: var(--ui-warn); }
.rs-onchip { color: var(--ui-accent); border-color: var(--ui-accent); }

/* The hook is the reason to pick this body; it outranks everything else on the
   pane and is styled to say so. */
.rs-hook {
  border-left: 2px solid var(--ui-accent); padding: 3px 0 3px 9px;
  font-size: 12px; color: var(--ui-fg); overflow-wrap: anywhere;
}

.rs-sec__h { font-size: var(--ui-fs-label); letter-spacing: .18em; text-transform: uppercase; color: var(--ui-fg-mute); margin-bottom: 5px; }
.rs-moves { display: flex; flex-direction: column; gap: 2px; }
.rs-move { position: relative; padding-left: 13px; font-size: 11px; color: var(--ui-fg-dim); overflow-wrap: anywhere; }
.rs-move::before { content: "▸"; position: absolute; left: 0; color: var(--ui-line-hot); }
.rs-chips { display: flex; flex-wrap: wrap; gap: 4px; }
.rs-chips .chip { color: var(--ui-accent-2); border-color: var(--ui-line-hot); }

.rs-note {
  font-size: var(--ui-fs-label); color: var(--ui-fg-dim); line-height: 1.5;
  border: 1px dashed var(--ui-line); padding: 6px 8px; overflow-wrap: anywhere;
}
.rs-lock { border: 1px solid var(--ui-warn); border-left-width: 3px; padding: 5px 8px; font-size: var(--ui-fs-label); color: var(--ui-fg-dim); overflow-wrap: anywhere; }
.rs-lock b { color: var(--ui-warn); font-weight: normal; letter-spacing: .16em; text-transform: uppercase; display: block; }

/* auto-fit rather than a fixed count: two cards side by side on a desktop, one
   per line at 480px, with no second media query to keep in sync. */
.rs-cards { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 6px; }
.rs-card {
  border: 1px solid var(--ui-line); background: var(--ui-panel-2);
  padding: 6px 8px; display: flex; flex-direction: column; gap: 3px;
  cursor: pointer; min-width: 0;
}
.rs-card:hover { border-color: var(--ui-line-hot); }
.rs-card[data-cursor="1"] { background: var(--ui-panel-3); }
.rs-card[data-on="1"] { border-color: var(--ui-accent); }
.rs-card[data-locked="1"] { opacity: .42; }
.rs-card__n { font-size: 11px; letter-spacing: .12em; text-transform: uppercase; }
.rs-card[data-on="1"] .rs-card__n { color: var(--ui-accent); }
.rs-card__f { font-size: var(--ui-fs-label); color: var(--ui-accent-2); overflow-wrap: anywhere; }
.rs-card__d { font-size: var(--ui-fs-label); color: var(--ui-fg-dim); overflow-wrap: anywhere; }

.rs-ab { display: flex; align-items: flex-start; gap: 6px; }
/* nowrap: "[ ]" is three glyphs and it broke across two lines inside the card. */
.rs-ab__x { flex: none; white-space: nowrap; color: var(--ui-fg-mute); }
.rs-card[data-on="1"] .rs-ab__x { color: var(--ui-accent); }

/* The price of an opt-in modifier is warn-coloured and always on the card. An
   optional challenge whose downside you have to hover to find is a trap. */
.rs-mod__cost { color: var(--ui-warn); }

/* The Ash shop (#10).

   Ash is deliberately *not* the accent colour the rest of this screen uses for
   "chosen": accent means "this is your pick for the run", and a permanent
   purchase is a different kind of commitment. It takes the secondary accent and
   contrast currency is printed beside the balance in muted text, so the screen
   says what each one is instead of leaving the player to infer it from two
   numbers that look the same. */
.rs-bank {
  display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap;
  border: 1px solid var(--ui-line); border-left: 2px solid var(--ui-accent-2);
  padding: 5px 9px; margin-bottom: 6px;
}
.rs-bank__n { font-size: 13px; color: var(--ui-accent-2); letter-spacing: .12em; }
.rs-bank__b { font-size: var(--ui-fs-label); color: var(--ui-fg-dim); overflow-wrap: anywhere; }
.rs-bank__vs { font-size: var(--ui-fs-label); color: var(--ui-fg-mute); overflow-wrap: anywhere; }

.rs-shop .rs-card__n { color: var(--ui-fg); }
.rs-price { font-size: 11px; color: var(--ui-accent-2); letter-spacing: .1em; white-space: nowrap; }
/* Unaffordable rows dim but stay clickable: the refusal names the shortfall,
   and a dead button teaches nothing (same rule as the attunement picker). */
.rs-card[data-afford="0"] .rs-price { color: var(--ui-warn); }
.rs-card[data-afford="0"] { opacity: .62; }
.rs-shop__msg { font-size: var(--ui-fs-label); color: var(--ui-accent-2); overflow-wrap: anywhere; margin-top: 5px; }
.rs-shop__buy { font-size: var(--ui-fs-tiny); letter-spacing: .14em; align-self: flex-start; }

.rs-seed { display: flex; align-items: center; gap: 6px; font-size: var(--ui-fs-label); color: var(--ui-fg-mute); letter-spacing: .16em; text-transform: uppercase; }
.rs-seed b { color: var(--ui-fg); font-weight: normal; letter-spacing: .1em; text-transform: none; }
.rs-seed button { font-size: var(--ui-fs-tiny); letter-spacing: .12em; }
.rs-seed-in { width: 10ch; background: transparent; border: 1px solid var(--ui-line);
  color: var(--ui-fg); font: inherit; font-size: var(--ui-fs-label); letter-spacing: .1em;
  text-transform: none; padding: 1px 4px; border-radius: 2px; }
.rs-seed-in:focus { outline: none; border-color: var(--ui-accent); }
/* A locked seed still reads as a value, not as a disabled control: it is the
   number the next run will use, which is the opposite of unavailable. */
.rs-seed-in[readonly] { color: var(--ui-fg-mute); border-style: dashed; }
.rs-seed button[data-on='1'] { color: var(--ui-accent); border-color: var(--ui-accent); }
.rs-seed button:disabled { opacity: .4; cursor: default; }

.rs-sum { font-size: var(--ui-fs-label); color: var(--ui-fg-dim); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rs-start {
  padding: 3px 16px; letter-spacing: .22em; text-transform: uppercase;
  color: var(--ui-accent); border-color: var(--ui-accent);
}
.rs-start[disabled] { color: var(--ui-fg-mute); border-color: var(--ui-line); }

@media (max-width: 620px) {
  /* Keystroke hints are the first thing to go — the summary and the start
     button are the two things the footer cannot lose. */
  .rs-hints { display: none; }
}
`;

/** Idempotent, exactly like `injectStyle` — two screens must not duplicate it. */
export function injectRunSetupStyle(doc: Document = document): void {
  if (doc.getElementById(STYLE_ID)) return;
  const s = doc.createElement('style');
  s.id = STYLE_ID;
  s.textContent = RUNSETUP_CSS;
  doc.head.appendChild(s);
}

// ------------------------------------------------------------------ screen

const KIND_GLYPH: Record<ArchetypeKind, string> = { humanoid: '◆', weird: '✷' };
const LOCK_GLYPH = '⊘';

type Group = 'arch' | 'kit' | 'mode' | 'modifier' | 'ability' | 'shop';

/**
 * The sections in the order the eye reads them, which is the order the shoulder
 * buttons walk (`moveGroup`). Tab already walks this order, because it is the
 * DOM order; naming it here is what lets the pad agree with Tab rather than
 * inventing a second sequence for the same six things.
 */
const GROUP_ORDER: readonly Group[] = ['arch', 'kit', 'mode', 'modifier', 'ability', 'shop'];

export class RunSetupScreen {
  readonly root: HTMLElement;

  private hostEl: HTMLElement;
  private cb: RunSetupCallbacks;
  private vm: RunSetupVM | null = null;
  /** Content fingerprint; the lists rebuild only when the offer actually changed. */
  private sig = '';
  private ownsRootClass = false;

  private bodyHead: HTMLElement;
  private humanHead: HTMLElement;
  private weirdHead: HTMLElement;
  private humanHost: HTMLElement;
  private weirdHost: HTMLElement;
  private poolHuman: Pool<HTMLElement>;
  private poolWeird: Pool<HTMLElement>;

  private detail: HTMLElement;
  private kitHost: HTMLElement;
  private poolKit: Pool<HTMLElement>;
  private modeHost: HTMLElement;
  private poolMode: Pool<HTMLElement>;
  private abSection: HTMLElement;
  private abHead: TextBind;
  private abHost: HTMLElement;
  private poolAb: Pool<HTMLElement>;
  private abNote: HTMLElement;

  private modSection: HTMLElement;
  private modHost: HTMLElement;
  private poolMod: Pool<HTMLElement>;

  private shopSection: HTMLElement;
  private shopBank: HTMLElement;
  private shopHost: HTMLElement;
  private poolShop: Pool<HTMLElement>;
  private shopNote: HTMLElement;
  private shopMsg: HTMLElement;

  private seedInput: HTMLInputElement;
  private lockBtn: HTMLButtonElement;
  private rerollBtn: HTMLButtonElement;
  private sumText: TextBind;
  private startBtn: HTMLButtonElement;

  /** Visual order: humanoids, then weird. Parallel to `rows`. */
  private archOrder: ArchetypeChoiceVM[] = [];
  private rows: HTMLElement[] = [];
  private kitEls: HTMLElement[] = [];
  private modeEls: HTMLElement[] = [];
  private abEls: HTMLElement[] = [];
  private modEls: HTMLElement[] = [];
  private shopEls: HTMLElement[] = [];

  private archId: string | null = null;
  private kitId: string | null = null;
  private modeId: string | null = null;
  /** Pick order matters: at capacity the earliest pick is the one displaced. */
  private abIds: string[] = [];

  private cur: Record<Group, number> = {
    arch: 0, kit: 0, mode: 0, modifier: 0, ability: 0, shop: 0,
  };
  private group: Group = 'arch';

  constructor(host: HTMLElement, cb: RunSetupCallbacks = {}) {
    this.hostEl = host;
    this.cb = cb;
    injectStyle();
    injectRunSetupStyle();
    // The palette lives on `.ui-root`. UIRoot normally puts it there; standing
    // alone we add it ourselves and take it back on destroy.
    if (!host.closest('.ui-root')) {
      host.classList.add('ui-root');
      this.ownsRootClass = true;
    }

    this.root = el('div', 'ui-layer rs-layer');
    this.root.style.pointerEvents = 'auto';
    this.root.tabIndex = -1;
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Run setup');

    const panel = el('div', 'panel rs-panel');

    // ---- head: title, seed, reroll
    const head = el('div', 'panel__head');
    head.append(el('div', 'panel__title', 'Begin a run'));
    head.append(el('div', 'ui-grow'));
    const seed = el('div', 'rs-seed');
    // An input rather than a label, so the seed can be typed in. Kept as text
    // and not `type=number`: a uint32 seed is nine digits, and the spinner
    // arrows a number input adds are a control nobody wants on a value that
    // has no meaningful neighbour.
    this.seedInput = document.createElement('input');
    this.seedInput.className = 'rs-seed-in';
    this.seedInput.type = 'text';
    this.seedInput.inputMode = 'numeric';
    this.seedInput.setAttribute('aria-label', 'world seed');
    this.seedInput.maxLength = 10;
    // Commit on blur as well as Enter. A seed typed and then clicked away from
    // has been chosen just as deliberately as one typed and confirmed.
    this.seedInput.addEventListener('change', () => this.commitSeed());
    this.seedInput.addEventListener('blur', () => this.commitSeed());
    this.lockBtn = btn('seed-lock', 'lock');
    this.lockBtn.addEventListener('click', () =>
      this.cb.onToggleSeedLock?.(!(this.vm?.seedLocked ?? false)),
    );
    this.rerollBtn = btn('reroll', 'reroll');
    seed.append(el('span', '', 'seed'), this.seedInput, this.lockBtn, this.rerollBtn);
    head.append(seed);

    // ---- body: the archetype list, then everything the body is chosen against
    const body = el('div', 'panel__body rs-body');

    const list = el('div', 'col rs-list');
    this.bodyHead = el('div', 'col__head', 'Body');
    list.append(this.bodyHead);
    const listScroll = el('div', 'scroll');
    this.humanHead = el('div', 'rs-sechead', 'Humanoid');
    this.humanHost = el('div');
    this.weirdHead = el('div', 'rs-sechead', 'Weird');
    this.weirdHost = el('div');
    listScroll.append(this.humanHead, this.humanHost, this.weirdHead, this.weirdHost);
    list.append(listScroll);
    this.poolHuman = new Pool(this.humanHost, () => makeArchRow());
    this.poolWeird = new Pool(this.weirdHost, () => makeArchRow());

    const main = el('div', 'col rs-main');
    const mainScroll = el('div', 'scroll rs-detail');

    this.detail = el('div');
    this.detail.style.display = 'flex';
    this.detail.style.flexDirection = 'column';
    this.detail.style.gap = '12px';

    // Kit sits directly under the body: the two together are "what you are on
    // frame one", and world shape is a separate decision made after them.
    const kitSec = el('div');
    kitSec.append(el('div', 'rs-sec__h', 'Kit — what you are holding'));
    this.kitHost = el('div', 'rs-cards');
    kitSec.append(this.kitHost);
    this.poolKit = new Pool(this.kitHost, () => makeCard());

    const modeSec = el('div');
    modeSec.append(el('div', 'rs-sec__h', 'World shape'));
    this.modeHost = el('div', 'rs-cards');
    modeSec.append(this.modeHost);
    this.poolMode = new Pool(this.modeHost, () => makeCard());

    // Modifiers sit under world shape: both change what the generator makes,
    // and the difference is that these are the ones you asked for.
    this.modSection = el('div');
    this.modSection.append(el('div', 'rs-sec__h', 'World modifiers — opt in, each with a price'));
    this.modHost = el('div', 'rs-cards');
    this.modSection.append(this.modHost);
    this.poolMod = new Pool(this.modHost, () => makeCard('rs-ab-card'));
    this.modSection.hidden = true;

    this.abSection = el('div');
    const abH = el('div', 'rs-sec__h', 'Attunement');
    this.abHead = new TextBind(abH);
    this.abHost = el('div', 'rs-cards');
    this.abNote = el('div', 'rs-note');
    this.abSection.append(abH, this.abHost, this.abNote);
    this.poolAb = new Pool(this.abHost, () => makeCard('rs-ab-card'));

    // The shop sits last: everything above it is a decision about *this* run,
    // and this is the one section that outlives the run you are about to start.
    // Putting a permanent purchase among the run's choices is precisely the
    // conflation the two currencies are styled apart to prevent (#10).
    this.shopSection = el('div', 'rs-shop');
    this.shopSection.append(el('div', 'rs-sec__h', 'Ash — spent between runs, kept forever'));
    this.shopBank = el('div', 'rs-bank');
    this.shopHost = el('div', 'rs-cards');
    this.shopNote = el('div', 'rs-note');
    this.shopMsg = el('div', 'rs-shop__msg');
    this.shopSection.append(this.shopBank, this.shopHost, this.shopNote, this.shopMsg);
    this.poolShop = new Pool(this.shopHost, () => makeCard());
    this.shopSection.hidden = true;

    mainScroll.append(
      this.detail, kitSec, modeSec, this.modSection, this.abSection, this.shopSection,
    );
    main.append(mainScroll);
    body.append(list, main);

    // ---- foot: how to drive it, what will actually start, and the start
    const foot = el('div', 'panel__foot');
    const hints = el('div', 'rs-hints ui-row');
    hints.append(
      kbd('↑↓'),
      el('span', '', 'body'),
      kbd('Tab'),
      el('span', '', 'section'),
      kbd('Space'),
      el('span', '', 'pick'),
      kbd('Enter'),
      el('span', '', 'begin'),
      // The pad's three, spelled out: a controller-only player cannot discover
      // that the shoulders replace Tab, and Enter-means-begin is why A is not
      // Enter here. See `SHELL_KEYS` in ui/menuPad.ts.
      kbd('LB/RB'),
      el('span', '', 'section'),
      kbd('A'),
      el('span', '', 'pick'),
      kbd('X'),
      el('span', '', 'begin'),
    );
    const sum = el('div', 'rs-sum ui-grow');
    this.sumText = new TextBind(sum);
    const backBtn = btn('◂ Title', 'back');
    backBtn.addEventListener('click', () => this.cb.onBack?.());
    this.startBtn = btn('Begin run', 'start', 'rs-start');
    foot.append(hints, sum, backBtn, this.startBtn);

    panel.append(head, body, foot);
    this.root.append(el('div', 'scrim'), panel);
    host.append(this.root);

    this.wire();
  }

  // ------------------------------------------------------------- lifecycle

  show(): void {
    this.root.hidden = false;
    this.root.focus();
    this.focusCursor(false);
  }

  hide(): void {
    this.root.hidden = true;
  }

  destroy(): void {
    this.poolHuman.dispose();
    this.poolWeird.dispose();
    this.poolKit.dispose();
    this.poolMode.dispose();
    this.poolAb.dispose();
    this.poolMod.dispose();
    this.poolShop.dispose();
    this.root.remove();
    if (this.ownsRootClass) this.hostEl.classList.remove('ui-root');
  }

  // ------------------------------------------------------------- data

  update(vm: RunSetupVM): void {
    this.vm = vm;
    // Do not stamp over what the player is in the middle of typing. Everything
    // else on this screen is a click, so a re-render can rewrite it freely; a
    // text field is the one control with in-progress state of its own.
    if (document.activeElement !== this.seedInput) this.seedInput.value = String(vm.seed);
    this.seedInput.readOnly = vm.seedLocked;
    this.lockBtn.textContent = vm.seedLocked ? 'locked' : 'lock';
    this.lockBtn.dataset.on = vm.seedLocked ? '1' : '0';
    this.lockBtn.setAttribute('aria-pressed', vm.seedLocked ? 'true' : 'false');
    // Disabled rather than hidden: a reroll button that vanishes when you lock
    // the seed reads as a bug, and a locked seed you can still reroll is not
    // locked.
    this.rerollBtn.disabled = vm.seedLocked;

    const sig = signature(vm);
    if (sig !== this.sig) {
      this.sig = sig;
      this.reconcileChoice(vm);
      this.renderList(vm);
      this.renderKits(vm);
      this.renderModes(vm);
      this.renderModifiers(vm);
      this.renderAbilities(vm);
      this.renderShop(vm);
    }
    this.paint();
    this.renderDetail();
    this.renderSummary();
  }

  /**
   * Keep the choice legal across an update: an unlock (or a save-file load) can
   * change what exists under us, and a start button pointing at a body that is
   * no longer offered is the kind of bug that only shows up on the run itself.
   */
  private reconcileChoice(vm: RunSetupVM): void {
    const playable = vm.archetypes.filter((a) => !a.locked);
    if (!this.archId || !playable.some((a) => a.id === this.archId)) {
      this.archId = playable[0]?.id ?? null;
    }
    this.kitId = defaultKitId(vm.kits, this.kitId);
    const modes = vm.worldModes.filter((m) => !m.locked);
    if (!this.modeId || !modes.some((m) => m.id === this.modeId)) {
      this.modeId = modes[0]?.id ?? null;
    }
    const offered = new Set(vm.attunement.available.filter((a) => !a.locked).map((a) => a.id));
    this.abIds = this.abIds.filter((id) => offered.has(id)).slice(0, vm.attunement.slots);
  }

  // ------------------------------------------------------------- list

  private renderList(vm: RunSetupVM): void {
    this.archOrder = [
      ...vm.archetypes.filter((a) => a.kind !== 'weird'),
      ...vm.archetypes.filter((a) => a.kind === 'weird'),
    ];
    this.rows.length = 0;

    const counts = { humanoid: 0, weird: 0 };
    this.poolHuman.begin();
    this.poolWeird.begin();
    for (const a of this.archOrder) {
      const weird = a.kind === 'weird';
      counts[weird ? 'weird' : 'humanoid']++;
      const row = (weird ? this.poolWeird : this.poolHuman).next();
      row.dataset.id = a.id;
      row.dataset.locked = a.locked ? '1' : '0';
      const glyph = row.firstElementChild as HTMLElement;
      const body = row.lastElementChild as HTMLElement;
      glyph.textContent = a.locked ? LOCK_GLYPH : KIND_GLYPH[a.kind];
      (body.firstElementChild as HTMLElement).textContent = a.name;
      (body.lastElementChild as HTMLElement).textContent = a.movement.join(', ');
      row.title = a.locked ? a.name + ' — locked' : a.special;
      this.rows.push(row);
    }
    this.poolHuman.end();
    this.poolWeird.end();

    this.bodyHead.textContent = `Body — ${counts.humanoid + counts.weird} ways to move`;
    this.humanHead.textContent = 'Humanoid · ' + counts.humanoid;
    this.weirdHead.textContent = 'Weird · ' + counts.weird;
    this.humanHead.hidden = counts.humanoid === 0;
    this.weirdHead.hidden = counts.weird === 0;

    const sel = this.archOrder.findIndex((a) => a.id === this.archId);
    this.cur.arch = clamp(sel >= 0 ? sel : 0, 0, this.rows.length - 1);
  }

  /** Locked kits render dimmed with their price, exactly like locked bodies. */
  private renderKits(vm: RunSetupVM): void {
    this.kitEls.length = 0;
    this.poolKit.begin();
    for (const k of vm.kits) {
      const card = this.poolKit.next();
      card.dataset.id = k.id;
      card.dataset.locked = k.locked ? '1' : '0';
      clear(card);
      card.append(el('div', 'rs-card__n', k.name));
      card.append(el('div', 'rs-card__d', k.desc));
      if (k.locked && k.lockHint) card.append(el('div', 'rs-card__d ui-warn', k.lockHint));
      this.kitEls.push(card);
    }
    this.poolKit.end();
    const sel = vm.kits.findIndex((k) => k.id === this.kitId);
    this.cur.kit = clamp(sel >= 0 ? sel : 0, 0, Math.max(0, this.kitEls.length - 1));
  }

  private renderModes(vm: RunSetupVM): void {
    this.modeEls.length = 0;
    this.poolMode.begin();
    for (const m of vm.worldModes) {
      const card = this.poolMode.next();
      card.dataset.id = m.id;
      card.dataset.locked = m.locked ? '1' : '0';
      clear(card);
      card.append(el('div', 'rs-card__n', m.name));
      card.append(el('div', 'rs-card__f', m.feel));
      card.append(el('div', 'rs-card__d', m.description));
      if (m.locked && m.lockHint) card.append(el('div', 'rs-card__d ui-warn', m.lockHint));
      this.modeEls.push(card);
    }
    this.poolMode.end();
    const sel = vm.worldModes.findIndex((m) => m.id === this.modeId);
    this.cur.mode = clamp(sel >= 0 ? sel : 0, 0, this.modeEls.length - 1);
  }

  /**
   * Opt-in world modifiers (#7).
   *
   * Checkbox cards rather than the single-choice cards above, because these are
   * the one group on the screen where the answers are not mutually exclusive —
   * and because a toggle is what makes them *opt-in* rather than a roll. The
   * price is on the card next to the effect: an optional challenge whose
   * downside you have to hover to read is a trap, not a choice.
   */
  private renderModifiers(vm: RunSetupVM): void {
    const mods = vm.modifiers;
    this.modEls.length = 0;
    this.modSection.hidden = !mods || mods.length === 0;
    this.poolMod.begin();
    for (const m of mods ?? []) {
      const card = this.poolMod.next();
      card.dataset.id = m.id;
      card.dataset.locked = m.locked ? '1' : '0';
      clear(card);
      const line = el('div', 'rs-ab');
      line.append(el('span', 'rs-ab__x', '[ ]'));
      line.append(el('span', 'rs-card__n', m.name));
      card.append(line, el('div', 'rs-card__d', m.desc));
      card.append(el('div', 'rs-card__f rs-mod__cost', 'price: ' + m.cost));
      if (m.locked && m.lockHint) card.append(el('div', 'rs-card__d ui-warn', m.lockHint));
      this.modEls.push(card);
    }
    this.poolMod.end();
    this.cur.modifier = clamp(this.cur.modifier, 0, Math.max(0, this.modEls.length - 1));
  }

  /**
   * With 0 slots the row still renders — an absent picker reads as a bug, an
   * explained one reads as a thing to go and earn (docs/05 §4, Attunement).
   */
  private renderAbilities(vm: RunSetupVM): void {
    const { slots, available } = vm.attunement;
    this.abEls.length = 0;
    this.poolAb.begin();
    if (slots > 0) {
      for (const a of available) {
        const card = this.poolAb.next();
        card.dataset.id = a.id;
        card.dataset.locked = a.locked ? '1' : '0';
        clear(card);
        const line = el('div', 'rs-ab');
        line.append(el('span', 'rs-ab__x', '[ ]'));
        line.append(el('span', 'rs-card__n', a.name));
        card.append(line, el('div', 'rs-card__d', a.desc));
        if (a.locked && a.lockHint) card.append(el('div', 'rs-card__d ui-warn', a.lockHint));
        this.abEls.push(card);
      }
    }
    this.poolAb.end();
    this.abHost.hidden = slots === 0;
    this.abNote.hidden = slots > 0;
    if (slots === 0) {
      this.abNote.textContent =
        'No attunement slots yet. Traversal abilities are found in the run itself — ' +
        'the first run of a profile is the Metroidvania. Boss kills feed the Attunement ' +
        'track, and each slot turns an ability you have already earned into a run-start option.';
    }
    this.cur.ability = clamp(this.cur.ability, 0, Math.max(0, this.abEls.length - 1));
  }

  // ------------------------------------------------------------- Ash shop

  /**
   * The between-runs spend screen (#10).
   *
   * `purchasables()`, `tryPurchase()` and `ashBalance()` were written and had
   * zero callers, so twelve Ash unlocks were permanently unbuyable and the only
   * currency you *spend* had nowhere to spend it. This is the surface.
   *
   * An unaffordable row renders dimmed but stays clickable, on the same rule the
   * attunement picker follows: the refusal names the shortfall ("costs 120 ash,
   * you have 40"), and a disabled button teaches nothing. Rows the profile is
   * not *allowed* to buy are a different matter and say so separately —
   * affordability and permission must never collapse into one greyed control.
   */
  private renderShop(vm: RunSetupVM): void {
    const shop = vm.shop;
    this.shopEls.length = 0;
    this.shopSection.hidden = !shop;
    if (!shop) {
      this.poolShop.begin();
      this.poolShop.end();
      return;
    }

    clear(this.shopBank);
    this.shopBank.append(el('div', 'rs-bank__n', currencyText(shop.balance, shop.currency)));
    this.shopBank.append(el('div', 'rs-bank__b', shop.currency.blurb));
    // The other currency is named here and nowhere else on this screen. Without
    // it "40" on the setup screen and "40" on the in-run purse are the same
    // number wearing the same clothes, and the player learns the difference by
    // losing one of them.
    this.shopBank.append(
      el('div', 'rs-bank__vs', 'not ' + shop.contrast.name + ' — ' + shop.contrast.blurb),
    );

    this.poolShop.begin();
    for (const e of shop.entries) {
      const card = this.poolShop.next();
      card.dataset.id = e.id;
      card.dataset.locked = '0';
      card.dataset.afford = e.affordable ? '1' : '0';
      clear(card);

      const head = el('div', 'rs-dhead');
      head.append(el('div', 'rs-card__n', e.name));
      head.append(el('div', 'ui-grow'));
      head.append(el('div', 'rs-price', priceText(e.cost, shop.currency)));
      card.append(head);
      // The grant first, the flavour second. "Start loud." is the right copy for
      // a kit list and the wrong copy for a price tag.
      card.append(el('div', 'rs-card__f', e.grant));
      card.append(el('div', 'rs-card__d', e.description));
      if (!e.affordable) {
        card.append(
          el('div', 'rs-card__d ui-warn', 'Short by ' + (e.cost - shop.balance) + ' ' + shop.currency.name),
        );
      }
      const buy = btn('Buy', 'buy', 'rs-shop__buy');
      buy.dataset.id = e.id;
      card.append(buy);
      this.shopEls.push(card);
    }
    this.poolShop.end();

    this.shopNote.hidden = shop.entries.length > 0;
    if (shop.empty) this.shopNote.textContent = shop.empty;
    this.shopMsg.hidden = !shop.message;
    this.shopMsg.textContent = shop.message ?? '';
    this.cur.shop = clamp(this.cur.shop, 0, Math.max(0, this.shopEls.length - 1));
  }

  // ------------------------------------------------------------- detail

  private renderDetail(): void {
    clear(this.detail);
    const a = this.archOrder[this.cur.arch];
    if (!a) {
      this.detail.append(el('div', 'rs-note', 'No bodies on offer.'));
      return;
    }

    const head = el('div', 'rs-dhead');
    head.append(el('div', 'rs-dname', a.name));
    head.append(el('span', 'chip', a.kind));
    if (a.locked) head.append(el('span', 'chip rs-lockchip', 'locked'));
    else if (a.id === this.archId) head.append(el('span', 'chip rs-onchip', 'chosen'));
    this.detail.append(head);

    this.detail.append(el('div', 'rs-hook', a.special));

    const moves = el('div');
    moves.append(el('div', 'rs-sec__h', 'How it moves'));
    const lines = el('div', 'rs-moves');
    for (const m of a.movement) lines.append(el('div', 'rs-move', m));
    if (a.movement.length === 0) lines.append(el('div', 'rs-move', 'Baseline movement, unaltered.'));
    moves.append(lines);
    this.detail.append(moves);

    const starts = el('div');
    starts.append(el('div', 'rs-sec__h', 'Starts with'));
    if (a.innate.length > 0) {
      const chips = el('div', 'rs-chips');
      for (const n of a.innate) chips.append(el('span', 'chip', n));
      starts.append(chips);
    } else {
      starts.append(el('div', 'rs-move ui-mute', 'Nothing. Every ability is found in the world.'));
    }
    this.detail.append(starts);

    if (a.locked) {
      const lock = el('div', 'rs-lock');
      lock.append(el('b', '', 'Locked'));
      lock.append(
        document.createTextNode(
          a.lockHint ?? 'Not yet earned. Lineage unlocks the bodies that kill you.',
        ),
      );
      const chosen = this.vm?.archetypes.find((x) => x.id === this.archId);
      if (chosen) lock.append(el('div', 'ui-mute', 'This run would start as ' + chosen.name + '.'));
      this.detail.append(lock);
    }
  }

  private renderSummary(): void {
    const arch = this.vm?.archetypes.find((a) => a.id === this.archId);
    const kit = this.vm?.kits.find((k) => k.id === this.kitId);
    const mode = this.vm?.worldModes.find((m) => m.id === this.modeId);
    const names = this.abIds.map(
      (id) => this.vm?.attunement.available.find((a) => a.id === id)?.name ?? id,
    );
    const mods = (this.vm?.modifiers ?? []).filter((m) => m.enabled).map((m) => m.name);
    const ready = !!arch && !!mode && !!kit;
    this.startBtn.disabled = !ready;
    this.sumText.set(
      ready
        ? arch!.name +
            ' · ' +
            kit!.name +
            ' · ' +
            mode!.name +
            ' · ' +
            (names.length ? names.join(' + ') : 'no attunement') +
            // Modifiers change what the generator makes, so the line that says
            // what will actually start has to name them (#7).
            ' · ' +
            (mods.length ? mods.join(' + ') : 'no modifiers') +
            ' · seed ' +
            (this.vm?.seed ?? 0)
        : 'No playable body on offer.',
    );
  }

  /** Cursor/selection paint. Attribute writes only — never a rebuild. */
  private paint(): void {
    for (let i = 0; i < this.rows.length; i++) {
      set(this.rows[i], 'cursor', this.group === 'arch' && i === this.cur.arch);
      set(this.rows[i], 'selected', this.archOrder[i]?.id === this.archId);
      this.rows[i].tabIndex = i === this.cur.arch ? 0 : -1;
    }
    for (let i = 0; i < this.kitEls.length; i++) {
      set(this.kitEls[i], 'cursor', this.group === 'kit' && i === this.cur.kit);
      set(this.kitEls[i], 'on', this.kitEls[i].dataset.id === this.kitId);
      this.kitEls[i].tabIndex = i === this.cur.kit ? 0 : -1;
    }
    for (let i = 0; i < this.modeEls.length; i++) {
      set(this.modeEls[i], 'cursor', this.group === 'mode' && i === this.cur.mode);
      set(this.modeEls[i], 'on', this.modeEls[i].dataset.id === this.modeId);
      this.modeEls[i].tabIndex = i === this.cur.mode ? 0 : -1;
    }
    for (let i = 0; i < this.abEls.length; i++) {
      const on = this.abIds.includes(this.abEls[i].dataset.id ?? '');
      set(this.abEls[i], 'cursor', this.group === 'ability' && i === this.cur.ability);
      set(this.abEls[i], 'on', on);
      this.abEls[i].tabIndex = i === this.cur.ability ? 0 : -1;
      const box = this.abEls[i].querySelector<HTMLElement>('.rs-ab__x');
      if (box) box.textContent = on ? '[×]' : '[ ]';
    }
    for (let i = 0; i < this.modEls.length; i++) {
      const on = this.vm?.modifiers?.[i]?.enabled ?? false;
      set(this.modEls[i], 'cursor', this.group === 'modifier' && i === this.cur.modifier);
      set(this.modEls[i], 'on', on);
      this.modEls[i].tabIndex = i === this.cur.modifier ? 0 : -1;
      const box = this.modEls[i].querySelector<HTMLElement>('.rs-ab__x');
      if (box) box.textContent = on ? '[×]' : '[ ]';
    }
    for (let i = 0; i < this.shopEls.length; i++) {
      set(this.shopEls[i], 'cursor', this.group === 'shop' && i === this.cur.shop);
      this.shopEls[i].tabIndex = i === this.cur.shop ? 0 : -1;
    }
    const slots = this.vm?.attunement.slots ?? 0;
    if (slots > 0) this.abHead.set('Attunement · ' + this.abIds.length + ' / ' + slots + ' chosen');
    else this.abHead.set('Attunement · no slots yet');
  }

  // ------------------------------------------------------------- input

  private wire(): void {
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-act="reroll"]')) {
        this.cb.onReroll?.();
        return;
      }
      if (t.closest('[data-act="start"]')) {
        this.start();
        return;
      }
      const buy = t.closest<HTMLElement>('[data-act="buy"]');
      if (buy?.dataset.id) {
        this.group = 'shop';
        this.cur.shop = Math.max(0, this.shopEls.findIndex((c) => c.dataset.id === buy.dataset.id));
        this.cb.onPurchase?.(buy.dataset.id);
        return;
      }
      const row = t.closest<HTMLElement>('.rs-arch');
      if (row) {
        this.group = 'arch';
        this.cur.arch = this.rows.indexOf(row);
        this.commitArch();
        return;
      }
      const card = t.closest<HTMLElement>('.rs-card');
      if (!card) return;
      const ki = this.kitEls.indexOf(card);
      if (ki >= 0) {
        this.group = 'kit';
        this.cur.kit = ki;
        this.commitKit();
        return;
      }
      const mi = this.modeEls.indexOf(card);
      if (mi >= 0) {
        this.group = 'mode';
        this.cur.mode = mi;
        this.commitMode();
        return;
      }
      const mo = this.modEls.indexOf(card);
      if (mo >= 0) {
        this.group = 'modifier';
        this.cur.modifier = mo;
        this.toggleModifier();
        return;
      }
      const ai = this.abEls.indexOf(card);
      if (ai >= 0) {
        this.group = 'ability';
        this.cur.ability = ai;
        this.toggleAbility();
        return;
      }
      // A bare click on a shop card moves the cursor there and nothing else.
      // Spending is on the row's own Buy button — every other card on this
      // screen commits on click, and a permanent purchase must not.
      const si = this.shopEls.indexOf(card);
      if (si >= 0) {
        this.group = 'shop';
        this.cur.shop = si;
        this.paint();
        this.focusCursor(true);
      }
    });

    // Tab moves between groups for free because each group exposes exactly one
    // tabbable element (roving tabindex); this keeps `group` honest afterwards.
    this.root.addEventListener('focusin', (e) => {
      const t = e.target as HTMLElement;
      if (t.classList.contains('rs-arch')) this.group = 'arch';
      else if (this.kitEls.includes(t)) this.group = 'kit';
      else if (this.modeEls.includes(t)) this.group = 'mode';
      else if (this.modEls.includes(t)) this.group = 'modifier';
      else if (this.abEls.includes(t)) this.group = 'ability';
      else if (this.shopEls.includes(t)) this.group = 'shop';
      else return;
      this.paint();
    });

    this.root.addEventListener('keydown', (e) => {
      // Modal: nothing behind this screen may see a keystroke while it is up.
      e.stopPropagation();
      if (this.handleKey(e)) e.preventDefault();
    });
  }

  /**
   * Public so an integrator routing through core/input.ts can drive the screen
   * without a second window listener. The root listener already calls it.
   */
  handleKey(e: KeyboardEvent): boolean {
    if (e.altKey || e.ctrlKey || e.metaKey) return false;
    // The seed field owns every key while it has focus, except the two that
    // mean "I am done with it". Without this, typing a seed would move the
    // archetype cursor on every arrow and start a run on the first digit that
    // happened to be Enter — the screen's own navigation eating a text field
    // is the same two-consumers-one-input defect as #67, #78 and #82.
    if (document.activeElement === this.seedInput) {
      if (e.key === 'Enter') {
        this.commitSeed();
        this.seedInput.blur();
        return true;
      }
      if (e.key === 'Escape') {
        // Abandon the edit rather than the screen: restore what the VM says
        // and hand the next Escape to `onBack` normally.
        this.seedInput.value = String(this.vm?.seed ?? 0);
        this.seedInput.blur();
        return true;
      }
      return false;
    }
    // #162: every real `<button>` on this screen — Back, Reroll, Lock, every
    // shop row's Buy — is `type="button"` with its own click handling (a
    // direct listener, or `wire()`'s delegated `[data-act]` dispatch), which
    // is exactly what a native Enter/Space already fires on a focused button.
    // Letting that run, instead of intercepting here, is what stops Enter on
    // Back from starting a run and Space on Reroll from misdispatching
    // through `activate()`'s `group`-based switch (`focusin` below never
    // assigns `group` for a button, since a button is not one of the six
    // roving-tabindex groups) — the two defects had the same root, one
    // switch case each treating every focused element as a group member.
    // This used to special-case `rerollBtn`/`lockBtn` inside `'Enter'` for
    // exactly this reason; the general form here covers Buy and Back too, so
    // that per-button carve-out is gone rather than left unreachable.
    //
    // The deferral is only correct for a *real* keystroke, though — issue
    // #193. `handlePadKey` feeds this a `padKeyEvent()` record, which is a
    // plain object cast to `KeyboardEvent` rather than a real one (see its
    // doc), and nothing ever dispatches it, so there is no native click for
    // deferring to wait for. A player who real-Tabbed to Back/Start with a
    // keyboard and then picked the pad back up would find B/X did nothing —
    // deferred to a native activation that was never going to happen — until
    // a direction key moved focus off the button. `instanceof KeyboardEvent`
    // is false for the synthetic record and true for a genuine `keydown`, so
    // this still defers to the real button for a real keystroke and lets the
    // pad through unconditionally, which is what it always needed.
    if (e instanceof KeyboardEvent && document.activeElement instanceof HTMLButtonElement) {
      return false;
    }
    switch (e.key) {
      case 'Enter':
        this.start();
        return true;
      case ' ':
        this.activate();
        return true;
      case 'ArrowDown':
        return this.move(this.group === 'mode' ? 0 : 1);
      case 'ArrowUp':
        return this.move(this.group === 'mode' ? 0 : -1);
      case 'ArrowRight':
        return this.move(this.group === 'arch' ? 0 : 1);
      case 'ArrowLeft':
        return this.move(this.group === 'arch' ? 0 : -1);
      case 'Escape':
        this.cb.onBack?.();
        return true;
      case 'Home':
        return this.moveTo(0);
      case 'End':
        return this.moveTo(this.count() - 1);
      default:
        return false;
    }
  }

  /**
   * Drive the screen from a gamepad — issue #78.
   *
   * Straight through to `handleKey`, because unlike the settings screen this
   * one has no mode in which a keystroke means something a pad cannot mean.
   * The mapping from button to key is `SHELL_KEYS` in `ui/menuPad.ts`, and the
   * one it cannot express is `moveGroup` below.
   */
  handlePadKey(e: KeyboardEvent): void {
    this.handleKey(e);
  }

  /**
   * Step to the next non-empty section — the shoulder buttons, issue #78.
   *
   * Tab does this for the keyboard, and it does it *by not being handled*: each
   * group exposes one tabbable element, the browser moves focus, and `focusin`
   * notices. A pad cannot borrow that, because a synthesised Tab is untrusted
   * and moves nothing — so the ring the platform was drawing for free is drawn
   * here instead.
   *
   * Empty sections are skipped rather than landed on. A fresh profile has no
   * abilities, no world modifiers and an empty shop; stopping on one would
   * leave the cursor nowhere with no way to tell whether the pad had failed.
   * If *every* section is empty the group does not move, which is the honest
   * outcome for a screen with nothing on it.
   */
  moveGroup(delta: number): void {
    if (delta === 0) return;
    let at = GROUP_ORDER.indexOf(this.group);
    for (let step = 0; step < GROUP_ORDER.length; step++) {
      at = (at + delta + GROUP_ORDER.length) % GROUP_ORDER.length;
      const g = GROUP_ORDER[at];
      const n = this.groupEls(g).length;
      if (n === 0) continue;
      this.group = g;
      // A section can shrink between visits — buying the last shop row removes
      // it — and a stale index would leave `focusCursor` with nothing to focus.
      if (this.cur[g] >= n) this.cur[g] = n - 1;
      this.paint();
      this.focusCursor(true);
      return;
    }
  }

  /**
   * Reroll the seed from a pad — issue #193. Y/triangle, routed through
   * `ShellPad`'s `reroll` command (`ui/menuPad.ts`), since the reroll button
   * has no key of its own for `handleKey` to answer to.
   *
   * The same guard `rerollBtn.disabled` already draws: a locked seed cannot be
   * rerolled by mouse, and a pad bypassing `cb.onReroll` around that guard
   * would let Y do what the mouse cannot.
   */
  reroll(): void {
    if (this.rerollBtn.disabled) return;
    this.cb.onReroll?.();
  }

  /**
   * The elements of one group, as the single place that mapping lives.
   *
   * `count()` and `focusCursor()` each carried their own copy of this ternary
   * chain, so adding the shop as a fifth group meant editing the same fact in
   * two places and getting a silently un-navigable section if you missed one.
   */
  private groupEls(g: Group): HTMLElement[] {
    switch (g) {
      case 'arch':
        return this.rows;
      case 'kit':
        return this.kitEls;
      case 'mode':
        return this.modeEls;
      case 'modifier':
        return this.modEls;
      case 'ability':
        return this.abEls;
      default:
        return this.shopEls;
    }
  }

  private count(): number {
    return this.groupEls(this.group).length;
  }

  private move(d: number): boolean {
    if (d === 0) return false;
    return this.moveTo(this.cur[this.group] + d);
  }

  private moveTo(i: number): boolean {
    const n = this.count();
    if (n === 0) return false;
    const next = clamp(i, 0, n - 1);
    if (next === this.cur[this.group]) return true;
    this.cur[this.group] = next;
    // Moving is choosing (list-box semantics) for the two single-choice groups;
    // abilities are multi-select, so there the cursor only ever previews.
    if (this.group === 'arch') this.commitArch();
    else if (this.group === 'kit') this.commitKit();
    else if (this.group === 'mode') this.commitMode();
    else {
      // Abilities are multi-select and the shop spends a permanent currency:
      // in neither does moving the cursor commit anything. Buying by arrowing
      // past a row would be unforgivable in a screen where Enter means "begin".
      this.paint();
      this.focusCursor(true);
    }
    return true;
  }

  private activate(): void {
    if (this.group === 'arch') this.commitArch();
    else if (this.group === 'kit') this.commitKit();
    else if (this.group === 'mode') this.commitMode();
    else if (this.group === 'shop') this.purchase();
    else if (this.group === 'modifier') this.toggleModifier();
    else this.toggleAbility();
  }

  /**
   * Flip one modifier. A locked one may be read but never enabled, exactly like
   * a locked body — the reward you have not earned still has to be visible or
   * it is not a motive.
   */
  private toggleModifier(): void {
    const card = this.modEls[this.cur.modifier];
    const id = card?.dataset.id;
    if (!id || card.dataset.locked === '1') return;
    const on = !(this.vm?.modifiers?.find((m) => m.id === id)?.enabled ?? false);
    this.cb.onToggleModifier?.(id, on);
  }

  /**
   * Report the intent to buy. Deliberately fired even when the row is
   * unaffordable — `tryPurchase` answers with the shortfall in words, and a
   * button that does nothing and says nothing is the worse of the two failures.
   * A row whose *prerequisites* are unmet is refused here, because that refusal
   * has no reading in Ash at all.
   */
  private purchase(): void {
    const card = this.shopEls[this.cur.shop];
    const id = card?.dataset.id;
    if (!id || card.dataset.locked === '1') return;
    this.cb.onPurchase?.(id);
  }

  private commitArch(): void {
    const a = this.archOrder[this.cur.arch];
    // A locked body may be inspected but never chosen; selection stays put and
    // the footer keeps saying what will really start.
    if (a && !a.locked) this.archId = a.id;
    this.paint();
    this.renderDetail();
    this.renderSummary();
    this.focusCursor(true);
  }

  /** Same rule as a locked body: inspectable, never chosen. */
  private commitKit(): void {
    const k = this.vm?.kits[this.cur.kit];
    if (k && !k.locked) this.kitId = k.id;
    this.paint();
    this.renderSummary();
    this.focusCursor(true);
  }

  private commitMode(): void {
    const m = this.vm?.worldModes[this.cur.mode];
    if (m && !m.locked) this.modeId = m.id;
    this.paint();
    this.renderSummary();
    this.focusCursor(true);
  }

  private toggleAbility(): void {
    const slots = this.vm?.attunement.slots ?? 0;
    const card = this.abEls[this.cur.ability];
    const id = card?.dataset.id;
    if (!id || slots === 0 || card.dataset.locked === '1') return;
    const at = this.abIds.indexOf(id);
    if (at >= 0) this.abIds.splice(at, 1);
    else {
      // At capacity, displace the earliest pick rather than refusing the click:
      // a dead control teaches nothing, a swap teaches the limit immediately.
      if (this.abIds.length >= slots) this.abIds.shift();
      this.abIds.push(id);
    }
    this.paint();
    this.renderSummary();
    this.focusCursor(true);
  }

  private focusCursor(withFocus: boolean): void {
    const target = this.groupEls(this.group)[this.cur[this.group]];
    if (!target) return;
    if (withFocus) target.focus({ preventScroll: true });
    target.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Read the seed field and report it, or put back what was there.
   *
   * Reports only on a *change*, so blurring an untouched field does not look
   * like a choice — the integrator re-renders on every report and a spurious
   * one would fight the player's cursor.
   *
   * Rejection restores rather than clears. A field that empties itself when
   * you fumble a digit has thrown away the number you were editing, and the
   * seed is nine digits nobody has memorised.
   */
  private commitSeed(): void {
    if (!this.vm || this.vm.seedLocked) return;
    const parsed = parseSeed(this.seedInput.value);
    if (parsed === null || parsed === this.vm.seed) {
      this.seedInput.value = String(this.vm.seed);
      return;
    }
    this.cb.onSeedEntered?.(parsed);
  }

  private start(): void {
    if (!this.archId || !this.modeId || !this.kitId || !this.vm) return;
    this.cb.onStart?.({
      archetype: this.archId,
      kit: this.kitId,
      mode: this.modeId,
      abilities: this.abIds.slice(),
      seed: this.vm.seed,
    });
  }
}

// ------------------------------------------------------------------ helpers

function makeArchRow(): HTMLElement {
  const row = el('div', 'rs-arch');
  row.tabIndex = -1;
  row.setAttribute('role', 'option');
  const body = el('div', 'rs-arch__b');
  body.append(el('div', 'rs-arch__n'), el('div', 'rs-arch__s'));
  row.append(el('div', 'rs-arch__g'), body);
  return row;
}

function makeCard(extra = ''): HTMLElement {
  const c = el('div', 'rs-card' + (extra ? ' ' + extra : ''));
  c.tabIndex = -1;
  c.setAttribute('role', 'option');
  return c;
}

function kbd(label: string): HTMLElement {
  const k = document.createElement('kbd');
  k.textContent = label;
  return k;
}

function set(e: HTMLElement, key: string, on: boolean): void {
  const v = on ? '1' : '0';
  if (e.dataset[key] !== v) e.dataset[key] = v;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** What the offer *is*, not what is chosen — chosen state survives a rebuild. */
/**
 * A typed seed, or `null` if it is not one.
 *
 * Decimal only, and masked to uint32 with `>>> 0` because that is what
 * `rollSeed` produces and what the generator consumes — a seed outside that
 * range would silently become a different world than the number on screen.
 *
 * Rejects rather than clamps on overflow: `4294967296` becoming `0` is the
 * kind of quiet substitution that has somebody comparing two runs they believe
 * share a seed. `Number.parseInt` is not used for the same reason — it would
 * read `12abc` as 12.
 */
export function parseSeed(raw: string): number | null {
  const s = raw.trim();
  if (!/^\d{1,10}$/.test(s)) return null;
  const n = Number(s);
  if (!Number.isInteger(n) || n < 0 || n > 0xffffffff) return null;
  return n >>> 0;
}

function signature(vm: RunSetupVM): string {
  const shop = vm.shop;
  return (
    vm.archetypes.map((a) => a.id + (a.locked ? '!' : '')).join(',') +
    '|' +
    vm.kits.map((k) => k.id + (k.locked ? '!' : '')).join(',') +
    '|' +
    vm.worldModes.map((m) => m.id + (m.locked ? '!' : '')).join(',') +
    '|' +
    vm.attunement.slots +
    ':' +
    vm.attunement.available.map((a) => a.id + (a.locked ? '!' : '')).join(',') +
    // `enabled` is in the fingerprint, unlike every other chosen state on this
    // screen: a modifier's pick lives on the *profile*, not on the panel, so a
    // toggle arrives as a changed offer rather than as local state to preserve.
    '|' +
    (vm.modifiers ?? [])
      .map((m) => m.id + (m.locked ? '!' : '') + (m.enabled ? '+' : ''))
      .join(',') +
    // The balance and the message are in the fingerprint as well as the list:
    // a purchase that buys the last item leaves the entries identical and the
    // balance and the confirmation are the only evidence it happened, and a
    // refusal changes nothing at all except the sentence explaining it.
    '|' +
    (shop
      ? shop.balance +
        ':' +
        (shop.message ?? '') +
        ':' +
        shop.entries.map((e) => e.id + (e.affordable ? '' : '!')).join(',')
      : '')
  );
}
